import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { ExportFileFormat } from '@/types';
import {
  ExportPriceQuoteMutationFn,
  useExportPriceQuoteMutation,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/documents/export-price-quote.generated';
import { openWebBrowserURL } from '@/utils/navigation';
import { useCallback, useRef } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

interface UseExportQuoteMutatorArgs {
  chatId: string | null;
  quoteId: string | null;
}

interface UseExportQuoteMutatorResult {
  exportCsv: () => Promise<void>;
  exportPdf: () => Promise<void>;
  isExportingCsv: boolean;
  isExportingPdf: boolean;
}

/**
 * Renders a saved price quote as a PDF or CSV via the `exportPriceQuote`
 * mutation, then opens the resulting document URL in a new browser tab.
 *
 * Uses two independent mutation instances (one per format) so PDF and CSV
 * track loading state separately — a shared instance would disable both
 * buttons whenever either is in flight.
 *
 * The mutation runs inside `openWebBrowserURL`'s async-resolver form so that
 * a tab is opened synchronously inside the user's click gesture; without
 * this, Safari's popup blocker rejects the post-await `window.open`.
 *
 * Exports are no-ops when `chatId` or `quoteId` is null. Callers should
 * disable their trigger UI in that state rather than relying on the silent
 * bail-out.
 *
 * @param args - Mutator inputs
 * @param args.chatId - Chat the quote belongs to; required by the action's permission check
 * @param args.quoteId - Saved quote to export
 * @returns Format-specific export handlers and independent loading flags
 *
 * @example
 * ```ts
 * const { exportPdf, exportCsv, isExportingPdf, isExportingCsv } =
 *   useExportQuoteMutator({
 *     chatId: state.account?.chatId ?? null,
 *     quoteId: state.quoteId,
 *   });
 *
 * <Button onPress={exportPdf} disabled={isExportingPdf} />
 * ```
 */
export const useExportQuoteMutator = ({
  chatId,
  quoteId,
}: UseExportQuoteMutatorArgs): UseExportQuoteMutatorResult => {
  const { showErrorToast } = useExperienceToasts();
  const [exportPdfMutation, { loading: isExportingPdf }] =
    useExportPriceQuoteMutation();
  const [exportCsvMutation, { loading: isExportingCsv }] =
    useExportPriceQuoteMutation();

  // Synchronous guards prevent rage-clicks from firing two requests in the
  // window between mutation start and the loading flag rendering.
  const pdfInFlight = useRef(false);
  const csvInFlight = useRef(false);

  const runExport = useCallback(
    async (
      mutation: ExportPriceQuoteMutationFn,
      fileFormat: ExportFileFormat,
    ) => {
      if (!chatId || !quoteId) {
        return;
      }

      try {
        await openWebBrowserURL(async () => {
          const { data } = await mutation({
            variables: { chatId, fileFormat, priceQuoteId: quoteId },
          });
          const url = data?.exportPriceQuote?.document_url;
          if (!url) {
            throw new Error('exportPriceQuote returned no document_url');
          }
          return url;
        });
      } catch (error) {
        captureException(error);
        showErrorToast(i18n.t('sales_portal.price_quotes.export_error'));
      }
    },
    [chatId, quoteId, showErrorToast],
  );

  const exportPdf = useCallback(async () => {
    if (pdfInFlight.current) {
      return;
    }
    pdfInFlight.current = true;
    try {
      await runExport(exportPdfMutation, ExportFileFormat.Pdf);
    } finally {
      pdfInFlight.current = false;
    }
  }, [exportPdfMutation, runExport]);

  const exportCsv = useCallback(async () => {
    if (csvInFlight.current) {
      return;
    }
    csvInFlight.current = true;
    try {
      await runExport(exportCsvMutation, ExportFileFormat.Csv);
    } finally {
      csvInFlight.current = false;
    }
  }, [exportCsvMutation, runExport]);

  return { exportCsv, exportPdf, isExportingCsv, isExportingPdf };
};
